import React, { useState } from "react";
import { gaTrackCta } from "../../analytics/ga4";
import { pageGutterPx } from "../../core/breakpoints";
import { C } from "../../core/constants";
import { APP_FONT_SIZE } from "../../core/theme";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { runtimeConfig } from "../../config/runtime";
import { CREDIT_COST_REFERENCE, CREDIT_PACK_BASE, SUBSCRIPTION_PRODUCT_CODE } from "../../features/billing/config";
import { localizedPath, localizedPathWithQuery } from "../../core/localePaths";
import { useI18n } from "../../features/i18n/LocaleProvider";
import { displayPrice } from "../../core/pricingDisplay";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";
import GoldText from "../visual/GoldText";

function CreditToken({ color, size = 20 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle at 35% 35%, ${color}ff, ${color}66)`,
        boxShadow: `0 0 10px ${color}99, 0 0 4px ${color}cc inset`,
        flexShrink: 0,
      }}
    />
  );
}

export default function PricingSection({ acc, variant = "full", standalone = false, analyticsScreen = "pricing" }) {
  const { t, locale } = useI18n();
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const sectionPadding = isMobile ? `48px ${gutter}px` : "80px 40px";
  const screen = analyticsScreen;
  const [hoveredPackIndex, setHoveredPackIndex] = useState(null);

  const packColorByKey = {
    stardust: acc.tertiary,
    nebula: acc.primary,
    supernova: acc.secondary,
  };
  const creditPacks = CREDIT_PACK_BASE.map((pack) => ({
    code: pack.key,
    name: t(`pack_${pack.key}`),
    credits: pack.credits,
    price: displayPrice(pack.usdPrice, locale),
    color: packColorByKey[pack.key] || acc.primary,
    desc: t(`pack_${pack.key}_desc`),
    popular: pack.popular,
  }));

  const costs = CREDIT_COST_REFERENCE.map((item) => ({
    label: t(item.labelKey),
    cost: t(item.costKey),
    icon: item.icon,
  }));

  if (variant === "compact") {
    return (
      <div style={{ padding: isMobile ? `40px ${gutter}px` : "72px 40px", maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.2em", color: acc.primary, textTransform: "uppercase", marginBottom: 10 }}>
            {t("credits_and_plans")}
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(32px,4.4vw,48px)", fontWeight: 400, color: C.textPrimary }}>
            {t("choose_your")} <GoldText accent={acc.secondary}>{t("cosmic_path")}</GoldText>
          </h2>
          <p style={{ color: C.textSecondary, fontSize: fs, marginTop: 10 }}>{t("pricing_start_free_upgrade")}</p>
          <p style={{ color: C.textMuted, fontSize: 13, marginTop: 8 }}>{t("pricing_credits_subscription_notice")}</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 18, marginBottom: 30 }}>
          {creditPacks.map((p, i) => (
            <GlassCard key={i} style={{ padding: 24 }} hoverable accent={p.color} glow>
              <div style={{ fontSize: 12, letterSpacing: "0.12em", color: p.color, textTransform: "uppercase", marginBottom: 8 }}>{p.name}</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, color: C.textPrimary, marginBottom: 4 }}>
                {p.credits} <span style={{ fontSize: 17, color: C.textMuted }}>{t("credits")}</span>
              </div>
              <div style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.55, minHeight: 44 }}>{p.desc}</div>
              <div style={{ marginTop: 14, fontSize: 20, color: p.color, fontFamily: "'Cormorant Garamond',serif" }}>{p.price}</div>
            </GlassCard>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <Button
            variant="primary"
            acc={acc}
            onClick={() => {
              const pricingDest = localizedPath("pricing", locale);
              gaTrackCta({
                cta_id: "pricing_view_full",
                cta_label: t("view_full_pricing"),
                destination: pricingDest,
                screen_class: screen,
              });
              window.location.assign(pricingDest);
            }}
            style={{ padding: "12px 30px", fontSize: 14 }}
          >
            {t("view_full_pricing")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: sectionPadding, maxWidth: 1160, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 60 }}>
        {standalone && (
          <div style={{ fontSize: 12, letterSpacing: "0.2em", color: acc.primary, textTransform: "uppercase", marginBottom: 12 }}>
            {t("credits_and_plans")}
          </div>
        )}
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(36px,5vw,56px)", fontWeight: 400, color: C.textPrimary }}>
          {t("choose_your")} <GoldText accent={acc.secondary}>{t("cosmic_path")}</GoldText>
        </h2>
        <p style={{ color: C.textSecondary, fontSize: fs, marginTop: 12 }}>{t("pricing_full_subtitle")}</p>
        <p style={{ color: C.textMuted, fontSize: 13, marginTop: 8 }}>{t("pricing_full_notice")}</p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          padding: "20px 32px",
          borderRadius: 16,
          marginBottom: 40,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 28, color: acc.primary }}>✦</div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary }}>{t("free_forever")}</div>
            <div style={{ fontSize: fs - 2, color: C.textSecondary, marginTop: 2 }}>{t("free_forever_desc")}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: acc.primary, border: `1px solid ${acc.primary}44`, padding: "6px 18px", borderRadius: 20 }}>
          {t("no_credit_card_needed")}
        </div>
      </div>

      <div style={{ fontSize: 12, letterSpacing: "0.18em", color: C.textMuted, textTransform: "uppercase", marginBottom: 20 }}>
        {t("credit_packs_top_up")}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20, marginBottom: 48 }}>
        {creditPacks.map((p, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredPackIndex(i)}
            onMouseLeave={() => setHoveredPackIndex(null)}
            style={{
              position: "relative",
              background: p.popular ? `linear-gradient(160deg,${acc.primary}18,${acc.tertiary}0d)` : "rgba(255,255,255,0.03)",
              border: `1px solid ${
                hoveredPackIndex === i ? `${p.color}77` : p.popular ? `${acc.primary}55` : "rgba(255,255,255,0.08)"
              }`,
              borderRadius: 20,
              padding: 28,
              backdropFilter: "blur(18px)",
              boxShadow:
                hoveredPackIndex === i
                  ? `0 0 48px ${p.color}33, 0 0 18px ${p.color}44`
                  : p.popular
                    ? `0 0 40px ${acc.primary}22`
                    : "none",
              transform: hoveredPackIndex === i ? "translateY(-2px)" : "translateY(0)",
              transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
            }}
          >
            {p.popular && (
              <div
                style={{
                  position: "absolute",
                  top: -13,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: `linear-gradient(90deg,${acc.primary},${acc.tertiary})`,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  padding: "4px 18px",
                  borderRadius: 20,
                  whiteSpace: "nowrap",
                }}
              >
                {t("best_value")}
              </div>
            )}
            <div style={{ fontSize: 12, letterSpacing: "0.12em", color: p.color, textTransform: "uppercase", marginBottom: 6 }}>{p.name}</div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", margin: "12px 0 16px" }}>
              {Array.from({ length: p.credits }).map((_, j) => (
                <CreditToken key={j} color={p.color} size={14} />
              ))}
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, fontWeight: 500, color: C.textPrimary, marginBottom: 2 }}>
              {p.credits} <span style={{ fontSize: 18, color: C.textMuted }}>{t("credits")}</span>
            </div>
            <div style={{ fontSize: fs - 2, color: C.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>{p.desc}</div>
            <button
              type="button"
              onClick={() => {
                if (runtimeConfig.authContext.signedIn) {
                  const paymentDest = localizedPathWithQuery("payment", locale, {
                    product_code: p.code,
                    product_type: "credit_pack",
                  });
                  gaTrackCta({
                    cta_id: `pricing_pack_${p.code}`,
                    cta_label: p.name,
                    destination: paymentDest,
                    screen_class: screen,
                    product_code: p.code,
                    product_type: "credit_pack",
                  });
                  window.location.assign(paymentDest);
                } else {
                  const signupDest = localizedPath("signUp", locale);
                  gaTrackCta({
                    cta_id: `pricing_pack_${p.code}_signup`,
                    cta_label: p.name,
                    destination: signupDest,
                    screen_class: screen,
                    product_code: p.code,
                    product_type: "credit_pack",
                  });
                  window.location.assign(signupDest);
                }
              }}
              style={{
                width: "100%",
                borderRadius: 10,
                background: p.popular ? `linear-gradient(135deg,${acc.primary},${acc.tertiary})` : "transparent",
                border: p.popular ? "none" : `1px solid ${p.color}55`,
                color: p.popular ? "#fff" : p.color,
                fontFamily: "'DM Sans',sans-serif",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 18px",
              }}
            >
              <span>{t("get_pack", `Get ${p.name}`).replace("%{name}", p.name)}</span>
              <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20 }}>{p.price}</span>
            </button>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, letterSpacing: "0.18em", color: C.textMuted, textTransform: "uppercase", marginBottom: 20 }}>
        {t("subscription_go_unlimited")}
      </div>
      <div
        style={{
          position: "relative",
          borderRadius: 24,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${acc.primary}22, ${acc.tertiary}14, ${acc.secondary}0d)`,
          border: `1px solid ${acc.secondary}55`,
          boxShadow: `0 0 80px ${acc.primary}22, 0 20px 60px rgba(0,0,0,0.4)`,
          backdropFilter: "blur(24px)",
          padding: "40px 48px",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 40,
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: 80,
            top: "50%",
            transform: "translateY(-50%)",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${acc.secondary}18, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.2em",
                color: acc.secondary,
                textTransform: "uppercase",
                background: `${acc.secondary}18`,
                border: `1px solid ${acc.secondary}44`,
                padding: "4px 14px",
                borderRadius: 20,
              }}
            >
              {t("eclipse_pass")}
            </div>
          </div>
          <div
            style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: "clamp(28px,4vw,44px)",
              fontWeight: 400,
              color: C.textPrimary,
              marginBottom: 16,
              lineHeight: 1.1,
            }}
          >
            {t("unlimited_cosmic_access")}
            <br />
            <GoldText accent={acc.secondary}>{t("every_single_day")}</GoldText>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10, marginBottom: 28 }}>
            {[
              t("perk_unlimited_zodiac"),
              t("perk_unlimited_tarot"),
              t("perk_wedding_planner"),
              t("perk_couple_match"),
              t("perk_priority_processing"),
              t("perk_preserve_credits"),
            ].map((perk, j) => (
              <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: fs - 1, color: C.textSecondary }}>
                <span style={{ color: acc.secondary, fontSize: 11 }}>✦</span>
                {perk}
              </div>
            ))}
          </div>
          <button
            type="button"
            style={{
              padding: "14px 40px",
              borderRadius: 12,
              background: `linear-gradient(135deg,${acc.secondary}cc,${acc.primary}cc)`,
              border: "none",
              color: "#fff",
              fontFamily: "'DM Sans',sans-serif",
              fontWeight: 600,
              fontSize: 16,
              cursor: "pointer",
              boxShadow: `0 0 40px ${acc.secondary}55`,
            }}
            onClick={() => {
              if (runtimeConfig.authContext.signedIn) {
                const paymentDest = localizedPathWithQuery("payment", locale, {
                  product_code: SUBSCRIPTION_PRODUCT_CODE,
                  product_type: "subscription",
                });
                gaTrackCta({
                  cta_id: "pricing_subscription_eclipse_pass",
                  cta_label: t("eclipse_pass"),
                  destination: paymentDest,
                  screen_class: screen,
                  product_code: SUBSCRIPTION_PRODUCT_CODE,
                  product_type: "subscription",
                });
                window.location.assign(paymentDest);
              } else {
                const signupDest = localizedPath("signUp", locale);
                gaTrackCta({
                  cta_id: "pricing_subscription_signup",
                  cta_label: t("eclipse_pass"),
                  destination: signupDest,
                  screen_class: screen,
                  product_type: "subscription",
                });
                window.location.assign(signupDest);
              }
            }}
          >
            {t("start_eclipse_pass").replace("%{price}", displayPrice("$9.99", locale))}
          </button>
        </div>

        <div style={{ textAlign: "center", position: "relative", flexShrink: 0 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 80, fontWeight: 300, color: C.textPrimary, lineHeight: 1 }}>
            {displayPrice("$9.99", locale)}
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>{t("per_month_cancel_anytime")}</div>
        </div>
      </div>

      <div
        style={{
          marginTop: 40,
          padding: "24px 32px",
          borderRadius: 16,
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.15em", color: C.textMuted, textTransform: "uppercase", marginBottom: 16 }}>
          {t("credit_costs_per_reading")}
        </div>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          {costs.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, color: acc.primary }}>{c.icon}</span>
              <span style={{ fontSize: fs - 1, color: C.textSecondary }}>{c.label}</span>
              <span style={{ fontSize: fs - 1, color: acc.primary, fontWeight: 500 }}>{c.cost}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
